import { lazy, Suspense, useEffect, useState } from "react";
import { Outlet, Link, createRootRouteWithContext, HeadContent, Scripts, useLocation } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { ScrollToTop } from "@/components/site/ScrollToTop";
import { MobileQuickActionBar } from "@/components/site/MobileQuickActionBar";
import { Toaster } from "@/components/ui/sonner";

const ChatBot = lazy(() => import("@/components/site/ChatBot").then((m) => ({ default: m.ChatBot })));

import { getSocialMeta } from "@/lib/social-meta.functions";
import { SOCIAL_META_DEFAULTS, SOCIAL_META_IMAGE } from "@/lib/social-meta";
import { trackPageView } from "@/lib/analytics";

import appCss from "../styles.css?url";

function NotFoundComponent() {
  return (
    <div className="flex min-h-[70vh] items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-display font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full bg-gradient-cta px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90 transition"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  loader: async () => {
    try {
      return { social: await getSocialMeta() };
    } catch {
      return { social: SOCIAL_META_DEFAULTS };
    }
  },
  head: ({ loaderData }) => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "WeLivdIt - US Visa Interview Coaching & Mock Interviews" },
      {
        name: "description",
        content:
          "1,500+ US student visa approvals since 2021. DS-160 strategy, mock interviews and 214(b) refusal recovery with WeLivdIt specialists.",
      },
      { name: "referrer", content: "strict-origin-when-cross-origin" },
      { name: "author", content: "WeLivdIt" },
      { name: "theme-color", content: "#0b1220" },
      { property: "og:site_name", content: "WeLivdIt" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: (loaderData?.social ?? SOCIAL_META_DEFAULTS).title },
      { property: "og:description", content: (loaderData?.social ?? SOCIAL_META_DEFAULTS).description },
      { name: "twitter:title", content: (loaderData?.social ?? SOCIAL_META_DEFAULTS).title },
      { name: "twitter:description", content: (loaderData?.social ?? SOCIAL_META_DEFAULTS).description },
      { property: "og:image", content: SOCIAL_META_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:type", content: "image/jpeg" },
      { property: "og:image:alt", content: "WeLivdIt - 1,500+ US visa approvals, 4.7 stars on Trustpilot" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: SOCIAL_META_IMAGE },
      { name: "google-site-verification", content: "6wMOp_Uhz12W5OUHDbVi9h9BEadWmgxOUFD678kPNOA" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500&display=swap",
      },
    ],
    scripts: [
      {
        children: `(function(){try{document.documentElement.classList.remove('dark');document.documentElement.style.colorScheme='light';localStorage.removeItem('welivdit:theme');}catch(e){}})();`,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" style={{ colorScheme: "light" }}>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const location = useLocation();
  const [showFloating, setShowFloating] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const mount = () => {
      if (!cancelled) setShowFloating(true);
    };

    // Mount on first user interaction (keeps the chatbot off the critical path)
    const events: (keyof WindowEventMap)[] = ["pointerdown", "scroll", "keydown", "touchstart"];
    const onInteract = () => {
      cleanup();
      mount();
    };
    const cleanup = () => events.forEach((e) => window.removeEventListener(e, onInteract));
    events.forEach((e) => window.addEventListener(e, onInteract, { passive: true, once: true }));

    // Fallback: idle-mount so bounce visitors still see the chat bubble
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    let idleId: number | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (w.requestIdleCallback) {
      idleId = w.requestIdleCallback(
        () => {
          cleanup();
          mount();
        },
        { timeout: 6000 },
      );
    } else {
      timeoutId = setTimeout(() => {
        cleanup();
        mount();
      }, 4500);
    }
    return () => {
      cancelled = true;
      cleanup();
      if (idleId !== undefined) w.cancelIdleCallback?.(idleId);
      if (timeoutId !== undefined) clearTimeout(timeoutId);
    };
  }, []);

  const isAdminOrDashboard = location.pathname === "/student-dashboard" || location.pathname.startsWith("/admin");

  // Page views for the admin growth dashboard (public pages only).
  useEffect(() => {
    if (isAdminOrDashboard) return;
    trackPageView(location.pathname);
  }, [location.pathname, isAdminOrDashboard]);

  return (
    <QueryClientProvider client={queryClient}>
    <div className="flex min-h-screen flex-col">
      {!isAdminOrDashboard && <Header />}
      <main className="flex-1">
        <Outlet />
      </main>
      {!isAdminOrDashboard && <Footer />}
      {!isAdminOrDashboard && <ScrollToTop />}
      {!isAdminOrDashboard && <MobileQuickActionBar />}
      {!isAdminOrDashboard && showFloating && (
        <Suspense fallback={null}>
          <ChatBot />
        </Suspense>
      )}
      <Toaster />
    </div>
    </QueryClientProvider>
  );
}
